/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2020 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {BaseRequestHandler} from "../../requestHandler/BaseRequestHandler";
import {getRequestConfigurationWithToken} from "../../requestHandler/getRequestConfigWithToken";
import {ICartData} from "../CartData";
import {ICartPromotion} from "../CartPromotion";
import {CurrencyCode} from "../CurrencyCode";
import timer from "../../logger/timer";
import {ApiError} from "./ApiError";
import {IWishlistData} from "../../wishlist/requestHandler/WishlistRequestHandler";
import {GtinItem} from "../../items/interface/Item.interface";

/**
 * A class for performing requests against the cart api
 */
export class CartRequestHandler {
  /**
   * Performs a request that returns the current cart or creates a new one
   * @param {number} shopId
   * @param {string} visitorId
   * @returns {Promise<ICartData>} - the cart data
   */
  @timer("API", "Cart")
  public static async createCart(shopId: number, visitorId: string): Promise<ICartData> {
    const configuration = await getRequestConfigurationWithToken<{visitorId: string}>();
    configuration.data = {visitorId};

    return BaseRequestHandler.performRequest<ICartData, {visitorId: string}>(
      "POST",
      `/shop/${shopId}/carts`,
      configuration,
    );
  }

  /**
   * Perform a request to get the cart for a specific visitor id
   * @param shopId
   * @param visitorId
   * @returns {Promise<ICartData|null>} - A promise resolving whether there is a cart or not
   */
  @timer("API", "Cart")
  public static async getCartForVisitorId(shopId: number, visitorId: string): Promise<ICartData | null> {
    const configuration = await getRequestConfigurationWithToken<{visitorId: string}>();
    configuration.data = {visitorId};

    return await BaseRequestHandler.performRequestWithPossibleError<ICartData, {visitorId: string}>(
      "GET",
      `/shop/${shopId}/visitors/${visitorId}/cart`,
      configuration,
    ).then((response) => {
      if (isError(response)) {
        if ((response as ApiError).originalError?.response?.status === 404) {
          // That 404 error is intended, and this error log comes from axios
          return null;
        }

        throw response;
      }

      return response;
    });
  }

  /**
   * Sends a request that returns the current cart status
   * @param {string} shopId - the id of the shop the cart is associated with
   * @param {string} cartId - the id of the cart
   * @returns {Promise<ICartData>} - resolves to the cart data
   */
  @timer("API", "Cart")
  public static async getCart(shopId: number, cartId: string): Promise<ICartData> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<ICartData>("GET", `/shop/${shopId}/carts/${cartId}`, configuration);
  }

  /**
   * Performs a request that adds a voucher to a cart
   * @param {string} shopId - the id of the shop the cart is associated with
   * @param {string} cartId - the id of the cart
   * @param {string} voucherCode - the code of the voucher to add
   * @returns {Promise<boolean>} - resolves to whether the operation was successful
   */
  @timer("API", "Cart")
  public static async addVoucher(shopId: number, cartId: string, voucherCode: string): Promise<ICartData> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<ICartData>(
      "PUT",
      `/shop/${shopId}/carts/${cartId}/voucher/${voucherCode}`,
      configuration,
    );
  }

  /**
   * Performs a request that remove a voucher from a cart
   * * @param {string} shopId - the id of the shop the cart is associated with
   * @param {number} shopId - the id of the sales channel of the used page
   * @param {string} cartId - the id of the cart to remove the voucher from
   * @param {string} voucherCode - the code of the voucher to remove
   * @returns {Promise<boolean>} - resolves to whether the operation was successful
   */
  @timer("API", "Cart")
  public static async removeVoucher(shopId: number, cartId: string, voucherCode: string): Promise<ICartData> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<ICartData>(
      "DELETE",
      `/shop/${shopId}/carts/${cartId}/voucher/${voucherCode}`,
      configuration,
    );
  }

  /**
   * Send a request that modifies user specific fields on the cart
   * @param {string} shopId - the shop the cart is associated with
   * @param {string} cartId - the id of the cart to modify
   * @param {ICartRequestHandlerPutCartOptions} options - the options with their new values
   * @returns {Promise<ICartData>} - resolves to the new cart data
   */
  @timer("API", "Cart")
  public static async putParams(
    shopId: number,
    cartId: string,
    options: ICartRequestHandlerPutCartOptions,
  ): Promise<ICartData> {
    const configuration = await getRequestConfigurationWithToken<ICartRequestHandlerPutCartOptions>();
    configuration.data = options;

    return BaseRequestHandler.performRequest<ICartData, ICartRequestHandlerPutCartOptions>(
      "PUT",
      `/shop/${shopId}/carts/${cartId}`,
      configuration,
    );
  }

  /**
   * Perform a request that adds an item to a given cart
   * @param {number} shopId - the id of the shop the cart is associated with
   * @param {string} cartId - the id of the cart to add the item to
   * @param {number} itemId - the id of the item to add
   * @param {number} quantity - the quantity of the item
   * @returns {Promise<boolean>} - A promise resolving to whether the action was successful
   */
  @timer("API", "Cart")
  public static async addItem(shopId: number, cartId: string, itemId: number, quantity: number): Promise<ICartData> {
    const configuration = await getRequestConfigurationWithToken<{quantity: number}>();
    configuration.data = {quantity};

    return BaseRequestHandler.performRequest<ICartData, {quantity: number}>(
      "PUT",
      `/shop/${shopId}/carts/${cartId}/items/${itemId}`,
      configuration,
    );
  }

  /**
   * Perform a request that adds an item to a given cart
   * @param {number} shopId - the id of the shop the cart is associated with
   * @param {string} cartId - the id of the cart to add the item to
   * @param {GtinItem[]} items - the itemId/gtin and quantity object array of the items to be added
   * @returns {Promise<ICartData>} - A promise resolving to whether the action was successful
   */
  @timer("API", "Cart")
  public static async addMultipleItems(shopId: number, cartId: string, items: GtinItem[]): Promise<ICartData> {
    const configuration = await getRequestConfigurationWithToken<GtinItem[]>();
    configuration.data = items;

    return BaseRequestHandler.performRequest<ICartData, GtinItem[]>(
      "PUT",
      `/shop/${shopId}/carts/${cartId}/items`,
      configuration,
    );
  }

  /**
   * Deletes an item identified by its itemId from the cart
   * @param {number} shopId - the id of the shop the cart is associated with
   * @param {string} cartId - the id of the cart to delete the item from
   * @param {number} itemId - the id of the item to delete
   * @returns {Promise<boolean>} - A promise resolving whether the action was successful
   */
  @timer("API", "Cart")
  public static async removeItem(shopId: number, cartId: string, itemId: number): Promise<ICartData> {
    const configuration = await getRequestConfigurationWithToken();

    return BaseRequestHandler.performRequest<ICartData>(
      "DELETE",
      `/shop/${shopId}/carts/${cartId}/items/${itemId}`,
      configuration,
    );
  }
}

export interface ICartRequestHandlerPutCartOptions {
  countryCode?: string;
  currencyCode?: CurrencyCode;
  isUsingPoints?: boolean;
  promotion?: ICartPromotion;
  isCorporateCustomer?: boolean;
  vatNumber?: string;
}

export const isError = (
  possibleError: IWishlistData | ICartData | Error | undefined | null,
): possibleError is Error => {
  return possibleError !== undefined && (possibleError as Error).message !== undefined;
};
